import React, { useMemo, useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three'

interface Props { title?: string; description?: string; onComplete?: () => void }

function Nucleons({ Z, N }: { Z: number; N: number }) {
  const grp = useRef<THREE.Group>(null)
  useFrame((_, dt) => { if (grp.current) grp.current.rotation.y += dt * 0.4 })
  const A = Z + N
  const rN = 0.54 // bán kính nucleon (chồng nhẹ → khối đặc, không rỗng)
  const nucleons = useMemo(() => {
    // Bán kính khối ~ A^(1/3), nén để các nucleon chạm/chồng nhẹ (hạt nhân ĐẶC)
    const R = 0.46 * Math.cbrt(Math.max(1, A))
    const golden = Math.PI * (1 + Math.sqrt(5))
    const arr: { x: number; y: number; z: number; proton: boolean }[] = []
    for (let i = 0; i < A; i++) {
      // i=0 ở TÂM, lấp đầy dần ra ngoài theo cbrt (mật độ thể tích đều, đặc ruột)
      const rr = A > 1 ? R * Math.cbrt(i / (A - 1)) : 0
      const phi = Math.acos(1 - 2 * (i + 0.5) / A)
      const theta = golden * i
      const proton = Math.round((i + 1) * Z / A) - Math.round(i * Z / A) === 1
      arr.push({ x: rr * Math.sin(phi) * Math.cos(theta), y: rr * Math.sin(phi) * Math.sin(theta), z: rr * Math.cos(phi), proton })
    }
    return arr
  }, [Z, N, A])
  return (
    <group ref={grp}>
      {nucleons.map((p, i) => (
        <mesh key={i} position={[p.x, p.y, p.z]}>
          <sphereGeometry args={[rN, 24, 24]} />
          <meshStandardMaterial color={p.proton ? '#ef4444' : '#3b82f6'} metalness={0.25} roughness={0.5} />
        </mesh>
      ))}
    </group>
  )
}

// Cấu trúc hạt nhân (Lớp 12 — Bài 18): hạt nhân gồm Z proton (đỏ) + N neutron (xanh),
// số khối A = Z + N. Kéo chuột để xoay khối hạt nhân 3D.
const NucleusStructureSimulation: React.FC<Props> = ({
  title = 'Cấu trúc hạt nhân (3D)',
  description = 'Hạt nhân gồm proton (đỏ) và neutron (xanh) liên kết chặt. Số khối A = Z + N; bán kính ~ A^(1/3).',
  onComplete,
}) => {
  const [Z, setZ] = React.useState(6)
  const [N, setN] = React.useState(6)
  const firstRef = React.useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }
  return (
    <div className="rounded-2xl border border-emerald-200 bg-white p-4 my-3">
      <h4 className="font-bold text-emerald-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="rounded-lg overflow-hidden border border-gray-100" style={{ height: 320, background: 'radial-gradient(circle at 50% 40%, #ffffff, #eef2f7)' }}>
        <Canvas camera={{ position: [0, 0, 7], fov: 50 }} onCreated={touch}>
          <ambientLight intensity={0.85} />
          <directionalLight position={[5, 6, 5]} intensity={0.9} />
          <pointLight position={[-5, -3, 2]} intensity={0.3} color="#93c5fd" />
          <Nucleons Z={Z} N={N} />
          <OrbitControls enablePan={false} />
        </Canvas>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 text-sm items-center">
        <label className="flex items-center gap-2"><span className="w-20 text-gray-600">Proton Z</span><input type="range" min={1} max={20} value={Z} onChange={(e) => { touch(); setZ(Number(e.target.value)) }} className="flex-1" /><span className="w-8 text-right font-semibold text-red-600">{Z}</span></label>
        <label className="flex items-center gap-2"><span className="w-20 text-gray-600">Neutron N</span><input type="range" min={0} max={24} value={N} onChange={(e) => setN(Number(e.target.value))} className="flex-1" /><span className="w-8 text-right font-semibold text-blue-600">{N}</span></label>
        <div className="text-center p-2 rounded-lg bg-emerald-50"><span className="font-black text-emerald-700">A = {Z + N}</span> <span className="text-xs text-gray-500">(Z={Z}, N={N})</span></div>
      </div>
    </div>
  )
}
export default NucleusStructureSimulation
